import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Crown, Swords, Plus, X, User, Clock, Flag, RotateCcw,
  MessageCircle, Users, Loader2, Check, Trophy, Handshake,
  ChevronLeft, RefreshCw, Coins
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish, getRelays } from '../hooks/useNostrPublish';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { timeAgo } from '../lib/utils';
import BCHTipButton from '../components/BCHTipButton';
import {
  type GameState, type Move, type Square, type Color, type PieceType, type GameResult,
  newGame, applyMove, legalMovesFrom, findLegalMove, getGameResult,
  isInCheck, sqToAlg, moveToUCI, parseUCI, pieceSymbol, file, rank, sq,
  toFEN,
} from '../lib/chess';

// ── Nostr event kinds for chess ─────────────────────────────────
// We use kind 1 with specific hashtags so games are discoverable
// from any Nostr client. Game data is in a 'chess' tag (or content for legacy).
const CHESS_TAG = 'bchnostr-chess';

/** Parse chess data from an event — checks 'chess' tag first, falls back to content */
function parseChessData(ev: any): any | null {
  try {
    // New format: data in 'chess' tag
    const chessTag = ev.tags?.find((t: string[]) => t[0] === 'chess');
    if (chessTag?.[1]) return JSON.parse(chessTag[1]);
    // Legacy format: data in content
    return JSON.parse(ev.content);
  } catch { return null; }
}

// ── Types ──────────────────────────────────────────────────────

interface Challenge {
  id: string;
  pubkey: string;
  createdAt: number;
  timeControl?: string; // e.g. "10min", "casual"
}

interface ActiveGame {
  challengeId: string; // the original challenge event id
  white: string; // pubkey
  black: string; // pubkey
  state: GameState;
  result: GameResult;
  myColor: Color;
}

// ── Main Page ──────────────────────────────────────────────────

export default function ChessPage() {
  const { user } = useAppContext();
  const [view, setView] = useState<'lobby' | 'game'>('lobby');
  const [activeGame, setActiveGame] = useState<ActiveGame | null>(null);

  const startGame = (game: ActiveGame) => {
    setActiveGame(game);
    setView('game');
  };

  const backToLobby = () => {
    setActiveGame(null);
    setView('lobby');
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          {view === 'game' && (
            <button onClick={backToLobby} className="p-1 rounded-lg hover:bg-muted -ml-1">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" /> BCH Chess
          </h1>
        </div>
      </div>

      {view === 'lobby' && <Lobby user={user} onStartGame={startGame} />}
      {view === 'game' && activeGame && (
        <GameView game={activeGame} setGame={setActiveGame} onBack={backToLobby} />
      )}
    </div>
  );
}

// ── Lobby ──────────────────────────────────────────────────────

function Lobby({ user, onStartGame }: {
  user: any;
  onStartGame: (game: ActiveGame) => void;
}) {
  const { publish } = useNostrPublish();
  const [creating, setCreating] = useState(false);

  // Fetch open challenges
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 3600 * 6); // last 6 hours
  const { events: challengeEvents, loading, refetch: refetchChallenges } = useNostrQuery({
    filters: [{ kinds: [1], '#t': [CHESS_TAG, 'challenge'], since, limit: 50 }],
    allRelays: true,
  });

  // Fetch accepted games (to filter out taken challenges + detect our active game)
  const { events: acceptEvents, refetch: refetchAccepts } = useNostrQuery({
    filters: [{ kinds: [1], '#t': [CHESS_TAG, 'accept'], since, limit: 50 }],
    allRelays: true,
  });

  // Also fetch move events so we can detect our active games
  const { events: moveEvents, refetch: refetchMoves } = useNostrQuery({
    filters: user ? [{ kinds: [1], '#t': [CHESS_TAG, 'move'], '#p': [user.pubkey], since, limit: 100 }] : [],
    enabled: !!user,
    allRelays: true,
  });

  // Poll for new challenges, accepts, and moves so the lobby stays fresh.
  // Without this, you'd never see someone accept your challenge unless you
  // manually refreshed (relays are unreliable with live event push).
  useEffect(() => {
    const interval = setInterval(() => {
      refetchChallenges();
      refetchAccepts();
      if (user) refetchMoves();
    }, 10000); // every 10 seconds in lobby
    return () => clearInterval(interval);
  }, [refetchChallenges, refetchAccepts, refetchMoves, user]);

  const acceptedChallengeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of acceptEvents) {
      const data = parseChessData(e);
      if (data?.challengeId) ids.add(data.challengeId);
    }
    return ids;
  }, [acceptEvents.length]);

  const challenges = useMemo(() => {
    const list: Challenge[] = [];
    for (const e of challengeEvents) {
      try {
        const data = parseChessData(e);
        if (!data || data.type !== 'challenge') continue;
        if (acceptedChallengeIds.has(e.id)) continue; // already taken
        list.push({
          id: e.id,
          pubkey: e.pubkey,
          createdAt: e.created_at,
          timeControl: data.timeControl || 'casual',
        });
      } catch {}
    }
    return list.sort((a, b) => b.createdAt - a.createdAt);
  }, [challengeEvents.length, acceptedChallengeIds.size]);

  // Check if user has an active game they should resume
  const myActiveGame = useMemo(() => {
    if (!user) return null;
    for (const e of acceptEvents) {
      try {
        const data = parseChessData(e);
        if (!data || data.type !== 'accept') continue;
        const white = data.white;
        const black = data.black;
        if (white !== user.pubkey && black !== user.pubkey) continue;

        // Rebuild game state from move events
        const gameId = data.challengeId;
        const gameMoves = moveEvents
          .filter(me => {
            const md = parseChessData(me);
            return md?.gameId === gameId && md?.type === 'move';
          })
          .sort((a, b) => a.created_at - b.created_at);

        let state = newGame();
        for (const me of gameMoves) {
          const md = parseChessData(me);
          if (md?.uci) {
            const parsed = parseUCI(md.uci);
            if (parsed) {
              const legal = findLegalMove(state, parsed.from, parsed.to, parsed.promotion);
              if (legal) state = applyMove(state, legal);
            }
          }
        }

        const result = getGameResult(state);
        // Check if game was resigned
        const resignEvent = moveEvents.find(me => {
          const md = parseChessData(me);
          return md?.gameId === gameId && md?.type === 'resign';
        });

        if (result || resignEvent) continue; // game is over

        return {
          challengeId: gameId,
          white,
          black,
          state,
          result: null,
          myColor: (user.pubkey === white ? 'w' : 'b') as Color,
        };
      } catch {}
    }
    return null;
  }, [user?.pubkey, acceptEvents.length, moveEvents.length]);

  const handleCreate = async () => {
    if (!user || creating) return;
    setCreating(true);
    try {
      await publish({
        kind: 1,
        content: '♟\uFE0F I\'m looking for a chess opponent! Come play BCH Chess on bchnostr.com/chess',
        tags: [['t', CHESS_TAG], ['t', 'challenge'], ['chess', JSON.stringify({ type: 'challenge', timeControl: 'casual' })]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch {}
    setCreating(false);
  };

  const handleAccept = async (challenge: Challenge) => {
    if (!user || challenge.pubkey === user.pubkey) return;

    // Challenger plays white (they move first), acceptor plays black
    const white = challenge.pubkey;
    const black = user.pubkey;

    try {
      const acceptData = { type: 'accept', challengeId: challenge.id, white, black };
      await publish({
        kind: 1,
        content: '♟\uFE0F Chess challenge accepted! Game on! bchnostr.com/chess',
        tags: [
          ['t', CHESS_TAG], ['t', 'accept'],
          ['e', challenge.id],
          ['p', challenge.pubkey],
          ['chess', JSON.stringify(acceptData)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      onStartGame({
        challengeId: challenge.id,
        white,
        black,
        state: newGame(),
        result: null,
        myColor: 'b', // acceptor always plays black
      });
    } catch {}
  };

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      {/* Hero */}
      <div className="text-center py-6">
        <div className="text-5xl mb-3">♟\uFE0F</div>
        <h2 className="text-xl font-bold">BCH Chess</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Play chess with the BCH community over Nostr
        </p>
      </div>

      {/* Resume active game */}
      {myActiveGame && (
        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold flex items-center gap-2">
                <Swords className="w-4 h-4 text-primary" /> Game in progress
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {myActiveGame.state.moves.length} moves played — it's {myActiveGame.state.turn === myActiveGame.myColor ? 'your' : "opponent's"} turn
              </p>
            </div>
            <button
              onClick={() => onStartGame(myActiveGame)}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              Resume
            </button>
          </div>
        </div>
      )}

      {/* Create challenge */}
      {user ? (
        <button
          onClick={handleCreate}
          disabled={creating}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Create Challenge
        </button>
      ) : (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">Log in to play chess</p>
        </div>
      )}

      {/* Open challenges */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
          <Swords className="w-4 h-4" /> Open Challenges
          {loading && <Loader2 className="w-3 h-3 animate-spin" />}
        </h3>

        {challenges.length === 0 && !loading && (
          <div className="text-center py-8 px-4">
            <Crown className="w-8 h-8 text-muted-foreground/15 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No open challenges</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Create one and wait for an opponent!</p>
          </div>
        )}

        <div className="space-y-2">
          {challenges.map(c => (
            <ChallengeCard
              key={c.id}
              challenge={c}
              isOwn={c.pubkey === user?.pubkey}
              onAccept={() => handleAccept(c)}
              canAccept={!!user && c.pubkey !== user?.pubkey}
            />
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="rounded-xl bg-muted/50 border border-border p-4 space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">How it works</p>
        <ul className="text-xs text-muted-foreground space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">1.</span>
            Create a challenge or accept someone else's
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">2.</span>
            Play moves on the board — every move is published to Nostr in real-time
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">3.</span>
            Win by checkmate, or your opponent resigns
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary font-bold">4.</span>
            Tip your opponent with BCH for a good game!
          </li>
        </ul>
      </div>
    </div>
  );
}

function ChallengeCard({ challenge, isOwn, onAccept, canAccept }: {
  challenge: Challenge;
  isOwn: boolean;
  onAccept: () => void;
  canAccept: boolean;
}) {
  const { profile } = useNostrProfile(challenge.pubkey);
  const name = profile?.display_name || profile?.name || challenge.pubkey.slice(0, 8) + '...';

  return (
    <div className={`rounded-xl border p-4 flex items-center justify-between ${isOwn ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'}`}>
      <div className="flex items-center gap-3">
        <Link to={`/profile/${challenge.pubkey}`}>
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
              <User className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
        </Link>
        <div>
          <p className="text-sm font-semibold">{name}</p>
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {challenge.timeControl === 'casual' ? 'Casual' : challenge.timeControl}
            <span className="mx-1">·</span>
            {timeAgo(challenge.createdAt)}
          </p>
        </div>
      </div>
      {isOwn ? (
        <span className="text-xs text-muted-foreground italic">Waiting...</span>
      ) : canAccept ? (
        <button
          onClick={onAccept}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-xs hover:bg-primary/90 transition-colors"
        >
          Play
        </button>
      ) : null}
    </div>
  );
}

// ── Game View ─────────────────────────────────────────────────

function GameView({ game, setGame, onBack }: {
  game: ActiveGame;
  setGame: (g: ActiveGame | null) => void;
  onBack: () => void;
}) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [selected, setSelected] = useState<Square | null>(null);
  const [legalTargets, setLegalTargets] = useState<Square[]>([]);
  const [showPromotion, setShowPromotion] = useState<{ from: Square; to: Square } | null>(null);
  const [lastMove, setLastMove] = useState<{ from: Square; to: Square } | null>(null);
  const [publishing, setPublishing] = useState(false);
  const opponentPubkey = game.myColor === 'w' ? game.black : game.white;
  const { profile: opponentProfile } = useNostrProfile(opponentPubkey);
  const { profile: myProfile } = useNostrProfile(user?.pubkey || '');
  const opponentName = opponentProfile?.display_name || opponentProfile?.name || opponentPubkey.slice(0, 8) + '...';
  const isMyTurn = game.state.turn === game.myColor;
  const flipped = game.myColor === 'b';

  // ── Listen for opponent moves ──
  // The relay subscription stays open after EOSE and should deliver new
  // events in real-time. However, many relays are unreliable with live
  // push, so we also poll on a timer (see below).
  const [moveSince] = useState(() => Math.floor(Date.now() / 1000) - 3600 * 6);
  const { events: moveEvents, refetch: refetchMoves } = useNostrQuery({
    filters: [{
      kinds: [1],
      '#t': [CHESS_TAG, 'move'],
      '#e': [game.challengeId],
      since: moveSince,
      limit: 200,
    }],
    allRelays: true,
  });

  // Also listen for resign events
  const { events: resignEvents, refetch: refetchResign } = useNostrQuery({
    filters: [{
      kinds: [1],
      '#t': [CHESS_TAG, 'resign'],
      '#e': [game.challengeId],
      since: moveSince,
      limit: 5,
    }],
    allRelays: true,
  });

  // ── Polling for opponent moves ─────────────────────────────────
  // Relays are supposed to push new events on open subscriptions, but
  // in practice many relays are unreliable (drop connections silently,
  // don't push events after EOSE, or have indexing delays for tag
  // filters). This caused the bug where both players were stuck waiting
  // for each other's move.
  //
  // Solution: poll every 3 seconds when it's NOT our turn (waiting for
  // opponent), and every 8 seconds when it IS our turn (in case our own
  // move didn't echo back). This is chess, not a chat app — the polling
  // rate is very reasonable.
  const pollIntervalRef = useRef<any>(null);
  useEffect(() => {
    if (game.result) {
      // Game over — stop polling
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      return;
    }

    const interval = isMyTurn ? 8000 : 3000;
    // Clear previous interval when turn changes
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(() => {
      refetchMoves();
      refetchResign();
    }, interval);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [isMyTurn, game.result, refetchMoves, refetchResign]);

  // Rebuild game state from all move events
  useEffect(() => {
    const allMoves = moveEvents
      .filter(e => {
        const d = parseChessData(e);
        return d?.type === 'move' && d?.gameId === game.challengeId;
      })
      .sort((a, b) => a.created_at - b.created_at);

    // Only rebuild if we have more moves than current state
    if (allMoves.length <= game.state.moves.length) return;

    let state = newGame();
    let last: { from: Square; to: Square } | null = null;
    for (const e of allMoves) {
      const d = parseChessData(e);
      if (d?.uci) {
        const parsed = parseUCI(d.uci);
        if (parsed) {
          const legal = findLegalMove(state, parsed.from, parsed.to, parsed.promotion);
          if (legal) {
            state = applyMove(state, legal);
            last = { from: legal.from, to: legal.to };
          }
        }
      }
    }

    const result = getGameResult(state);
    setGame({ ...game, state, result });
    if (last) setLastMove(last);
  }, [moveEvents.length]);

  // Check for resign
  useEffect(() => {
    const resign = resignEvents.find(e => {
      const d = parseChessData(e);
      return d?.type === 'resign' && d?.gameId === game.challengeId;
    });
    if (resign && !game.result) {
      setGame({ ...game, result: 'resign' });
    }
  }, [resignEvents.length]);

  const handleSquareClick = (s: Square) => {
    if (!isMyTurn || game.result) return;

    const piece = game.state.board[s];

    if (selected !== null) {
      // Try to make the move
      if (legalTargets.includes(s)) {
        // Check if this is a pawn promotion
        const movingPiece = game.state.board[selected];
        if (movingPiece?.type === 'p') {
          const promoRank = game.myColor === 'w' ? 7 : 0;
          if (rank(s) === promoRank) {
            setShowPromotion({ from: selected, to: s });
            return;
          }
        }
        makeMove(selected, s);
      } else if (piece && piece.color === game.myColor) {
        // Select a different piece
        setSelected(s);
        setLegalTargets(legalMovesFrom(game.state, s).map(m => m.to));
      } else {
        setSelected(null);
        setLegalTargets([]);
      }
    } else {
      if (piece && piece.color === game.myColor) {
        setSelected(s);
        setLegalTargets(legalMovesFrom(game.state, s).map(m => m.to));
      }
    }
  };

  const makeMove = async (from: Square, to: Square, promotion?: PieceType) => {
    const legal = findLegalMove(game.state, from, to, promotion);
    if (!legal) return;

    const newState = applyMove(game.state, legal);
    const result = getGameResult(newState);

    // Update local state immediately so the board shows the move
    setGame({ ...game, state: newState, result });
    setSelected(null);
    setLegalTargets([]);
    setShowPromotion(null);
    setLastMove({ from, to });
    setPublishing(true);

    // Publish move to Nostr
    try {
      const moveData = {
        type: 'move',
        gameId: game.challengeId,
        uci: moveToUCI(legal),
        san: legal.san,
        fen: toFEN(newState),
        moveNum: newState.moves.length,
      };
      await publish({
        kind: 1,
        content: `♟\uFE0F ${legal.san} (move ${newState.moves.length})`,
        tags: [
          ['t', CHESS_TAG], ['t', 'move'],
          ['e', game.challengeId],
          ['p', opponentPubkey],
          ['chess', JSON.stringify(moveData)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      // Refetch after a short delay so the move propagates to relays
      // and the opponent's polling picks it up faster
      setTimeout(() => refetchMoves(), 1000);
      setTimeout(() => refetchMoves(), 3000);
    } catch (err) {
      console.error('Failed to publish move:', err);
    } finally {
      setPublishing(false);
    }
  };

  const handleResign = async () => {
    if (!user || game.result) return;
    if (!confirm('Are you sure you want to resign?')) return;

    setGame({ ...game, result: 'resign' });

    try {
      const resignData = { type: 'resign', gameId: game.challengeId, by: user.pubkey };
      await publish({
        kind: 1,
        content: '♟\uFE0F I resigned. Good game!',
        tags: [
          ['t', CHESS_TAG], ['t', 'resign'],
          ['e', game.challengeId],
          ['p', opponentPubkey],
          ['chess', JSON.stringify(resignData)],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setTimeout(() => refetchResign(), 1000);
    } catch {}
  };

  // Determine winner for display
  let resultText = '';
  let winnerId = '';
  if (game.result === 'checkmate') {
    // The side that just moved delivered checkmate
    const winner = game.state.turn === 'w' ? 'b' : 'w';
    winnerId = winner === 'w' ? game.white : game.black;
    resultText = winnerId === user?.pubkey ? 'You won by checkmate!' : 'Checkmate — you lost';
  } else if (game.result === 'resign') {
    // Check who resigned — whoever's turn it was resigned (or check event)
    const resignEvent = resignEvents.find(e => {
      const d = parseChessData(e);
      return d?.gameId === game.challengeId;
    });
    const resignedBy = resignEvent ? resignEvent.pubkey : user?.pubkey;
    winnerId = resignedBy === game.white ? game.black : game.white;
    resultText = resignedBy === user?.pubkey ? 'You resigned' : 'Opponent resigned — you win!';
  } else if (game.result === 'stalemate') {
    resultText = 'Stalemate — draw';
  } else if (game.result === 'draw-50') {
    resultText = 'Draw by 50-move rule';
  } else if (game.result === 'draw-material') {
    resultText = 'Draw — insufficient material';
  }

  const inCheck = !game.result && isInCheck(game.state.board, game.state.turn);

  return (
    <div className="max-w-lg mx-auto p-4 space-y-3">
      {/* Opponent info */}
      <PlayerBar
        pubkey={opponentPubkey}
        profile={opponentProfile}
        color={game.myColor === 'w' ? 'b' : 'w'}
        isActive={!isMyTurn && !game.result}
        capturedPieces={getCapturedPieces(game.state, game.myColor === 'w' ? 'b' : 'w')}
      />

      {/* Board */}
      <div className="relative">
        <ChessBoard
          board={game.state.board}
          flipped={flipped}
          selected={selected}
          legalTargets={legalTargets}
          lastMove={lastMove}
          inCheck={inCheck ? game.state.turn : null}
          onSquareClick={handleSquareClick}
        />

        {/* Promotion picker */}
        {showPromotion && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-30 rounded-xl">
            <div className="bg-card rounded-xl border border-border p-4 space-y-2">
              <p className="text-xs font-semibold text-center text-muted-foreground">Promote to:</p>
              <div className="flex gap-2">
                {(['q', 'r', 'b', 'n'] as PieceType[]).map(pt => (
                  <button
                    key={pt}
                    onClick={() => makeMove(showPromotion.from, showPromotion.to, pt)}
                    className="w-14 h-14 rounded-lg bg-muted hover:bg-primary/20 flex items-center justify-center text-3xl transition-colors"
                  >
                    {pieceSymbol({ color: game.myColor, type: pt })}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Game result overlay */}
        {game.result && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-30 rounded-xl">
            <div className="bg-card rounded-xl border border-border p-6 text-center space-y-3 max-w-xs mx-4">
              {(game.result === 'checkmate' && winnerId === user?.pubkey) || (game.result === 'resign' && winnerId === user?.pubkey) ? (
                <Trophy className="w-10 h-10 text-amber-500 mx-auto" />
              ) : game.result === 'stalemate' || game.result?.startsWith('draw') ? (
                <Handshake className="w-10 h-10 text-muted-foreground mx-auto" />
              ) : (
                <Flag className="w-10 h-10 text-red-500 mx-auto" />
              )}
              <p className="text-sm font-bold">{resultText}</p>

              {/* Tip opponent after game */}
              {user && opponentPubkey && (
                <div className="flex flex-col items-center gap-1.5 pt-1">
                  <p className="text-[10px] text-muted-foreground">Good game? Tip your opponent!</p>
                  <BCHTipButton
                    recipientPubkey={opponentPubkey}
                    recipientProfile={opponentProfile}
                  />
                </div>
              )}

              <div className="flex gap-2 justify-center pt-1">
                <button
                  onClick={onBack}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-xs hover:bg-primary/90 transition-colors"
                >
                  Back to Lobby
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* My info */}
      <PlayerBar
        pubkey={user?.pubkey || ''}
        profile={myProfile}
        color={game.myColor}
        isActive={isMyTurn && !game.result}
        capturedPieces={getCapturedPieces(game.state, game.myColor)}
      />

      {/* Status + controls */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {!game.result && (
            publishing
              ? <span className="flex items-center gap-1 text-primary"><Loader2 className="w-3 h-3 animate-spin" /> Sending move...</span>
              : inCheck
                ? <span className="text-red-500 font-semibold">Check!</span>
                : isMyTurn
                  ? <span className="text-primary font-medium">Your move</span>
                  : <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Waiting for opponent&hellip;</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!game.result && !isMyTurn && (
            <button
              onClick={() => { refetchMoves(); refetchResign(); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Check for new moves"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          )}
          {!game.result && (
            <button
              onClick={handleResign}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-500/10 transition-colors"
            >
              <Flag className="w-3.5 h-3.5" /> Resign
            </button>
          )}
        </div>
      </div>

      {/* Move history */}
      {game.state.moves.length > 0 && (
        <MoveHistory moves={game.state.moves} />
      )}
    </div>
  );
}

// ── Chess Board Component ─────────────────────────────────────

function ChessBoard({ board, flipped, selected, legalTargets, lastMove, inCheck, onSquareClick }: {
  board: (import('../lib/chess').Piece | null)[];
  flipped: boolean;
  selected: Square | null;
  legalTargets: Square[];
  lastMove: { from: Square; to: Square } | null;
  inCheck: Color | null;
  onSquareClick: (sq: Square) => void;
}) {
  const ranks = flipped ? [0,1,2,3,4,5,6,7] : [7,6,5,4,3,2,1,0];
  const files = flipped ? [7,6,5,4,3,2,1,0] : [0,1,2,3,4,5,6,7];

  return (
    <div className="rounded-xl overflow-hidden border border-border shadow-lg select-none">
      {/* Rank labels + board */}
      <div className="flex">
        <div className="flex flex-col">
          {ranks.map(r => (
            <div key={r} className="w-4 flex items-center justify-center text-[9px] text-muted-foreground/50 font-mono" style={{ height: 'calc((100vw - 2rem - 8px) / 8)', maxHeight: '64px' }}>
              {r + 1}
            </div>
          ))}
        </div>
        <div className="flex-1 grid grid-cols-8 aspect-square">
          {ranks.map(r =>
            files.map(f => {
              const s = sq(f, r);
              const piece = board[s];
              const isLight = (f + r) % 2 === 1;
              const isSelected = s === selected;
              const isTarget = legalTargets.includes(s);
              const isLastFrom = lastMove?.from === s;
              const isLastTo = lastMove?.to === s;
              const isCheck = inCheck && piece?.type === 'k' && piece.color === inCheck;

              let bg = isLight ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-emerald-700/30 dark:bg-emerald-800/60';
              if (isSelected) bg = 'bg-yellow-300/70 dark:bg-yellow-600/50';
              else if (isLastFrom || isLastTo) bg = 'bg-yellow-200/50 dark:bg-yellow-700/30';
              if (isCheck) bg = 'bg-red-400/60 dark:bg-red-600/50';

              return (
                <button
                  key={s}
                  onClick={() => onSquareClick(s)}
                  className={`relative flex items-center justify-center ${bg} transition-colors`}
                  style={{ aspectRatio: '1' }}
                >
                  {/* Legal move indicator */}
                  {isTarget && !piece && (
                    <div className="w-[30%] h-[30%] rounded-full bg-black/15 dark:bg-white/20" />
                  )}
                  {isTarget && piece && (
                    <div className="absolute inset-0 border-[3px] border-black/20 dark:border-white/25 rounded-sm" />
                  )}

                  {/* Piece */}
                  {piece && (
                    <span
                      className="text-[clamp(1.5rem,6vw,2.5rem)] leading-none"
                      style={piece.color === 'w'
                        ? { color: '#fff', textShadow: '0 0 2px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,0.6)', WebkitTextStroke: '0.5px rgba(0,0,0,0.5)' }
                        : { color: '#1a1a2e', textShadow: '0 1px 2px rgba(0,0,0,0.4)', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))' }
                      }
                    >
                      {pieceSymbol(piece)}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
      {/* File labels */}
      <div className="flex pl-4">
        {files.map(f => (
          <div key={f} className="flex-1 text-center text-[9px] text-muted-foreground/50 font-mono py-0.5">
            {'abcdefgh'[f]}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Player Bar ────────────────────────────────────────────────

function PlayerBar({ pubkey, profile, color, isActive, capturedPieces }: {
  pubkey: string;
  profile: any;
  color: Color;
  isActive: boolean;
  capturedPieces: string;
}) {
  const name = profile?.display_name || profile?.name || (pubkey ? pubkey.slice(0, 8) + '...' : 'You');

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive ? 'bg-primary/5 border border-primary/20' : ''}`}>
      <div className="relative">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <User className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
        {/* Color indicator */}
        <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${
          color === 'w' ? 'bg-white' : 'bg-gray-800'
        }`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold truncate">{name}</p>
        {capturedPieces && (
          <p className="text-[10px] leading-tight opacity-60">{capturedPieces}</p>
        )}
      </div>
      {isActive && (
        <span className="text-[10px] font-medium text-primary flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          thinking
        </span>
      )}
    </div>
  );
}

// ── Move History ──────────────────────────────────────────────

function MoveHistory({ moves }: { moves: Move[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ left: scrollRef.current.scrollWidth, behavior: 'smooth' });
  }, [moves.length]);

  const pairs: { num: number; white?: string; black?: string }[] = [];
  for (let i = 0; i < moves.length; i += 2) {
    pairs.push({
      num: Math.floor(i / 2) + 1,
      white: moves[i]?.san,
      black: moves[i + 1]?.san,
    });
  }

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Moves</p>
      <div ref={scrollRef} className="flex gap-1 overflow-x-auto text-xs font-mono pb-1">
        {pairs.map(p => (
          <div key={p.num} className="flex items-center gap-1 shrink-0">
            <span className="text-muted-foreground/50">{p.num}.</span>
            <span className="font-medium">{p.white}</span>
            {p.black && <span className="font-medium">{p.black}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────

/** Get string of captured pieces by the given color */
function getCapturedPieces(state: GameState, capturedByColor: Color): string {
  // Count pieces that should exist vs what's on the board
  const initial: Record<string, number> = { p: 8, n: 2, b: 2, r: 2, q: 1 };
  const onBoard: Record<string, number> = { p: 0, n: 0, b: 0, r: 0, q: 0 };
  const oppColor = capturedByColor === 'w' ? 'b' : 'w';

  for (let i = 0; i < 64; i++) {
    const p = state.board[i];
    if (p && p.color === oppColor && p.type !== 'k') {
      onBoard[p.type] = (onBoard[p.type] || 0) + 1;
    }
  }

  let result = '';
  const order: import('../lib/chess').PieceType[] = ['q', 'r', 'b', 'n', 'p'];
  for (const t of order) {
    const captured = initial[t] - (onBoard[t] || 0);
    for (let i = 0; i < captured; i++) {
      result += pieceSymbol({ color: oppColor, type: t });
    }
  }
  return result;
}

